import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

/** COMPONENT */
import { Box, IconButton, MenuItem, Tooltip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SubjectIcon from "@mui/icons-material/Subject";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import HeaderCard from "../../component/cardCustom/headerCard";
import TableCustom from "../../component/table/tableCustom";
import TableRowCommon from "../../component/table/TableRowCommon";
import InputTextField from "../../component/input/inputTextField";
import FilterSelect from "../../component/select/filterSelect";
import ButtonCustom from "../../component/button/buttonCustom";
import Loading from "../../component/loading/loading";
import { notiError, notiSuccess } from "../../component/notification/notifications";
import { swalDelete } from "../../component/notification/swal";

/** MODAL */
import ModalDepositRefundCreate from "./modal/ModalDepositRefundCreate";
import ModalDepositRefundDetail from "./modal/ModalDepositRefundDetail";
import ModalDepositRefundLog from "./modal/ModalDepositRefundLog";

/** API */
import DepositRefundApi, { DepositRefundRow } from "../../api/deposit-refund";

/** UTILS */
import { colors } from "../../constants/colors";
import { MENUS_FIRST } from "../../constants/menu";
import {
  DEPOSIT_REFUND_FILTER_ALL,
  DEPOSIT_REFUND_STATUS,
  DEPOSIT_REFUND_STATUS_OPTIONS,
  depositRefundStatusBg,
  depositRefundStatusColor,
  depositRefundStatusLabel,
} from "../../constants/depositRefund";
import { dateTimeToView } from "../../utils/date.utils";
import { getApiErrorMessage } from "../../utils/api-error.utils";
import { DASH, amountToView } from "./depositRefund.utils";
import { useMenuPermission } from "./useMenuPermission";
import { useDepositRefundFilter } from "./useDepositRefundFilter";

/** STYLE */
import * as Style from "./depositRefund.style";

/** label ฝั่งผู้เช่าต่างจากฝั่งเจ้าหน้าที่ (WAITING = "รอเจ้าหน้าที่พิจารณา") */
const statusLabel = (status?: string) => depositRefundStatusLabel(status, true);

/** ฝั่งผู้เช่า — ส่งคำร้องขอคืนค่ามัดจำแผงของตัวเอง เลือกได้ทีละสัญญา */
export default function DepositRefundRequest() {
  const permissions = useMenuPermission(MENUS_FIRST.DEPOSIT_REFUND_REQUEST);
  const filter = useDepositRefundFilter("CUSTOMER");

  const [data, setData] = useState<DepositRefundRow[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCanceling, setIsCanceling] = useState<boolean>(false);

  /** modal — ใช้ standalone mode ทุกตัว จึงต้อง conditional render จากตรงนี้ */
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [logRow, setLogRow] = useState<DepositRefundRow | null>(null);

  const { buildTableParams } = filter;

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // base path คนละเส้นกับฝั่งเจ้าหน้าที่ — backend scope ด้วย token ให้เห็นแค่คำร้องของตัวเอง
      const res = await DepositRefundApi.findAll("CUSTOMER", buildTableParams());
      // envelope ของ endpoint ชุดนี้ซ้อน data สองชั้น ไม่ใช่ res.data ตรงๆ แบบ API รุ่นเก่า
      const paginated = res.data?.data;
      setData(paginated?.items || []);
      setRowCount(paginated?.meta?.totalItems || 0);
    } catch (error) {
      setData([]);
      setRowCount(0);
      notiError(getApiErrorMessage(error, "ดึงรายการคำร้องขอคืนค่ามัดจำไม่สำเร็จ"));
    } finally {
      setIsLoading(false);
    }
  }, [buildTableParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCancel = (objData: DepositRefundRow) => {
    swalDelete(
      "ยืนยันการยกเลิกคำร้อง",
      `ต้องการยกเลิกคำร้อง ${objData.deposit_refund_no || ""} ของสัญญา ${
        objData.lease_agreement_no || DASH
      } ใช่หรือไม่ (ยกเลิกแล้วยื่นคำร้องของสัญญานี้ใหม่ได้)`,
      async (confirm: boolean) => {
        if (!confirm || isCanceling) return;
        setIsCanceling(true);
        try {
          // ฝั่งลูกค้าใช้ PATCH /:id/cancel ไม่ใช่ DELETE (นั่นเป็นของเจ้าหน้าที่)
          // ยกเลิกแล้วแถวยังอยู่ในตาราง แต่สถานะเปลี่ยนเป็น CANCELED
          await DepositRefundApi.cancel(objData.id_deposit_refund);
          notiSuccess("ยกเลิกคำร้องสำเร็จ");
          loadData();
        } catch (error) {
          notiError(getApiErrorMessage(error, "ยกเลิกคำร้องไม่สำเร็จ"));
        } finally {
          setIsCanceling(false);
        }
      }
    );
  };

  const headCells = [
    { id: "no", disablePadding: false, label: "NO", align: "center" },
    { id: "depositRefundNo", disablePadding: false, label: "เลขที่คำร้อง", align: "center" },
    { id: "leaseAgreementNo", disablePadding: false, label: "เลขที่สัญญา", align: "center" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "left" },
    { id: "stallName", disablePadding: false, label: "แผงค้า", align: "left" },
    { id: "stallNumber", disablePadding: false, label: "เลขแผงค้า", align: "center" },
    {
      id: "approvedAmount",
      disablePadding: false,
      label: "ยอดเงินที่ได้รับการพิจารณา",
      align: "right",
    },
    { id: "requestDate", disablePadding: false, label: "วันเวลาที่ส่งคำร้อง", align: "center" },
    { id: "status", disablePadding: false, label: "สถานะ", align: "center" },
    { id: "action", disablePadding: false, label: "Action", align: "center" },
  ];

  const renderAction = (objData: DepositRefundRow) => {
    const isWaiting = objData.status === DEPOSIT_REFUND_STATUS.WAITING;
    const viewDisabled = !permissions.isView;
    // ยกเลิกได้เฉพาะคำร้องที่เจ้าหน้าที่ยังไม่พิจารณา
    const cancelDisabled = !isWaiting || !permissions.isDelete || isCanceling;

    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Tooltip title="รายละเอียดคำร้องขอคืนค่ามัดจำ">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setDetailId(objData.id_deposit_refund)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <DescriptionOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ประวัติการพิจารณา">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setLogRow(objData)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <SubjectIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={isWaiting ? "ยกเลิกคำร้อง" : "ยกเลิกได้เฉพาะคำร้องที่ยังรอเจ้าหน้าที่พิจารณา"}
        >
          <span>
            <IconButton
              disabled={cancelDisabled}
              onClick={() => handleCancel(objData)}
              sx={{ color: cancelDisabled ? colors.disabledGray : colors.red }}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const renderData = (objData: DepositRefundRow, no: number) => {
    const runningNo = filter.page * filter.pageLimit - filter.pageLimit + no + 1;

    const objRenderData = {
      key: String(objData.id_deposit_refund),
      id: String(objData.id_deposit_refund),
      obj: objData,
      columns: [
        { option: "TEXT", align: "center", label: runningNo },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.LinkText onClick={() => setDetailId(objData.id_deposit_refund)}>
              {objData.deposit_refund_no || DASH}
            </Style.LinkText>
          ),
        },
        { option: "TEXT", align: "center", label: objData.lease_agreement_no || DASH },
        { option: "TEXT", align: "left", label: objData.zone_name || DASH },
        { option: "TEXT", align: "left", label: objData.stall_name || DASH },
        { option: "TEXT", align: "center", label: objData.stall_number || DASH },
        {
          option: "TEXT",
          align: "right",
          /**
           * หัวคอลัมน์คือ "ยอดเงินที่ได้รับการพิจารณา" — ยังไม่พิจารณาต้องเป็นขีด
           * ห้าม fallback ไป request_amount เพราะจะกลายเป็นบอกผู้เช่าว่าได้เงินแล้ว
           * ดูยอดที่ขอคืนได้ใน modal รายละเอียด
           */
          // amountToView คืนขีดเองเมื่อไม่มีค่า (approved_amount เป็น string หรือ null)
          label: amountToView(objData.approved_amount),
        },
        // dateTimeToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
        {
          option: "TEXT",
          align: "center",
          label: objData.request_date ? dateTimeToView(objData.request_date) : DASH,
        },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.StatusChip
              chipColor={depositRefundStatusColor(objData.status)}
              chipBg={depositRefundStatusBg(objData.status)}
            >
              {statusLabel(objData.status)}
            </Style.StatusChip>
          ),
        },
        { option: "COMPONENT", align: "center", component: renderAction(objData) },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <>
      {(isLoading || isCanceling) && <Loading show type="softLoading" />}
      <HeaderCard text="ส่งคำร้องขอคืนค่ามัดจำแผง" />
      <Style.MainContainer>
        <Row className="align-items-end">
          <Col lg={3} md={6} xs={12} className="my-1">
            <InputTextField
              value={filter.searchInput}
              placeholder="ค้นหาเลขที่คำร้อง / เลขที่สัญญา / เลขแผงค้า"
              onchange={(e: any) => filter.setSearchInput(e.target.value)}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="deposit-refund-request-zone"
              selectId="deposit-refund-request-zone"
              value={filter.zoneId}
              onchange={(event: any) => filter.handleChangeZone(event.target.value)}
              renderValue={() => `โซน : ${filter.zoneLabel}`}
              options={[
                <MenuItem key="zone-all" value={DEPOSIT_REFUND_FILTER_ALL}>
                  ทั้งหมด
                </MenuItem>,
                ...filter.zones.map((zone) => (
                  <MenuItem key={zone.zoneId} value={zone.zoneId}>
                    {zone.zone_name || DASH}
                  </MenuItem>
                )),
              ]}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="deposit-refund-request-stall"
              selectId="deposit-refund-request-stall"
              value={filter.stallId}
              onchange={(event: any) => filter.handleChangeStall(event.target.value)}
              renderValue={() => `แผงค้า : ${filter.stallLabel}`}
              options={[
                <MenuItem key="stall-all" value={DEPOSIT_REFUND_FILTER_ALL}>
                  ทั้งหมด
                </MenuItem>,
                ...filter.stallOptions.map((stall) => (
                  <MenuItem key={stall.stallId} value={stall.stallId}>
                    {filter.stallOptionLabel(stall)}
                  </MenuItem>
                )),
              ]}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="deposit-refund-request-status"
              selectId="deposit-refund-request-status"
              value={filter.status}
              onchange={(event: any) => filter.handleChangeStatus(event.target.value)}
              renderValue={() => `สถานะ : ${statusLabel(filter.status)}`}
              options={DEPOSIT_REFUND_STATUS_OPTIONS.map((option: string) => (
                <MenuItem key={option} value={option}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: depositRefundStatusColor(option),
                      }}
                    />
                    {statusLabel(option)}
                  </Box>
                </MenuItem>
              ))}
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <ButtonCustom
              mode="add"
              style={{ width: "100%" }}
              textButton="ส่งคำร้องขอคืนค่ามัดจำ"
              disabled={!permissions.isCreate}
              onClick={() => setOpenCreate(true)}
            />
          </Col>
        </Row>

        <Style.TableContainer>
          <TableCustom
            headCells={headCells}
            customScroll
            page={filter.page}
            pageLimit={filter.pageLimit}
            sortType="ASC"
            sortBy=""
            rowCount={rowCount}
            rowsData={data.map((row, index) => renderData(row, index))}
            // API ชุดนี้ยังไม่รองรับการเรียงลำดับ หัวตารางจึงไม่มีปุ่ม sort
            onSort={() => {}}
            setPageLimit={filter.handleChangePageLimit}
            setPage={(value: number) => filter.setPage(value)}
            textEmptyData="ยังไม่มีคำร้องขอคืนค่ามัดจำ"
            tableMinWidth={1400}
          />
        </Style.TableContainer>
      </Style.MainContainer>

      {openCreate && (
        <ModalDepositRefundCreate
          onClose={() => setOpenCreate(false)}
          onSuccess={() => {
            setOpenCreate(false);
            filter.setPage(1);
            loadData();
          }}
        />
      )}

      {detailId !== null && (
        <ModalDepositRefundDetail
          id={detailId}
          side="CUSTOMER"
          onClose={() => setDetailId(null)}
        />
      )}

      {logRow && (
        <ModalDepositRefundLog
          id={logRow.id_deposit_refund}
          side="CUSTOMER"
          subTitle={[logRow.deposit_refund_no, logRow.lease_agreement_no]
            .filter(Boolean)
            .join(" · ")}
          onClose={() => setLogRow(null)}
        />
      )}
    </>
  );
}
